import { useEffect, useState } from 'react';
import type { ErpConnectionView, ErpInventoryRow } from '../../api';
import { erpApi } from '../../api';
import { useResource } from '../../hooks';
import { PanelCard, QuietEmpty, SectionHeader } from '../project/ui';
import { ErrorNotice, LoadingRows } from '../ui';
import { formatWhen } from '../distribution/labels';
import { Field, HelpTip, Input, Pager, Select } from '../primitives';
import { ResponsiveTable } from '../primitives/ResponsiveTable';
import type { Column } from '../primitives/ResponsiveTable';
import { Timestamp } from '../status/StatusPieces';
import { formatDecimal, formatNumber, linkedWebsites, severalStores, websiteKey, websiteLabel } from './labels';

const PAGE_SIZE = 50;

type InventoryFilter = '' | 'mapped' | 'unmapped' | 'missing';

const filterLabel: Record<InventoryFilter, string> = {
  '': 'All articles',
  mapped: 'Matched to a store product',
  unmapped: 'No matched products',
  missing: 'No longer reported',
};

/**
 * The price read from an article's own record (CO-185): both figures the
 * record gave, when it was read, and, when the last read failed, that it
 * failed (the price shown is then the one read before, and its age is the
 * line below it). A record is read only for products a
 * store matched, so an unmatched article says that rather than "no price".
 */
function CardPriceCell({ row }: { row: ErpInventoryRow }) {
  const card = row.card_price;
  if (!card) return null;
  const hasPrice = card.gross !== undefined || card.net !== undefined;
  return (
    <span className="rt-num tabular-nums" data-testid="erp-card-price">
      {card.gross !== undefined ? (
        <>
          {formatDecimal(card.gross, 2)}
          <span className="cell-stack__secondary">with VAT</span>
        </>
      ) : null}
      {card.net !== undefined ? (
        <span className="cell-stack__secondary">
          {formatDecimal(card.net, 2)} without VAT
        </span>
      ) : null}
      {!hasPrice && card.state === 'read' ? <span className="muted">No price in this list</span> : null}
      {!hasPrice && card.state === 'not_read' ? (
        <span className="muted">{row.products.length > 0 ? 'Not read yet' : 'Read only for matched products'}</span>
      ) : null}
      {/* The price shown, if any, is the one read before: its age is the line below. */}
      {card.state === 'not_found' || card.state === 'failed' ? (
        <span className="erp-missing">{card.state === 'not_found' ? 'Not found in the ERP' : 'Last read failed'}</span>
      ) : null}
      {card.read_at ? (
        <span className="cell-stack__secondary">
          Read <Timestamp iso={card.read_at} />
        </span>
      ) : null}
    </span>
  );
}

/**
 * Inventory: what the ERP last said, article by article and warehouse by
 * warehouse, and which store products each position feeds with what was
 * last sent. The mechanism is in the view's help (CO-158).
 */
export function ErpInventoryPanel({ connection, initialQuery = '' }: { connection: ErpConnectionView; initialQuery?: string }) {
  const [filter, setFilter] = useState<InventoryFilter>('');
  const [warehouse, setWarehouse] = useState('');
  // An issue's product link opens this view already searched (UX-19).
  const [search, setSearch] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery(search.trim());
      setOffset(0);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  const inventory = useResource(
    () => erpApi.inventory(connection.id, { filter, warehouse, q: query, limit: PAGE_SIZE, offset }),
    [connection.id, filter, warehouse, query, offset, connection.last_stock_sync_success_at],
    connection.id, { key: `erp:${connection.id}:inventory:${filter}:${warehouse}:${query}:${offset}` },
  );
  // Products name their website once more than one takes stock from this
  // connection (CO-134), and their store once several stores do.
  const linked = linkedWebsites(connection);
  const rows = inventory.data?.items ?? [];
  const total = inventory.data?.total ?? 0;
  const counts = inventory.data?.counts ?? {};
  const warehouses = inventory.data?.warehouses ?? connection.warehouses;
  const withStore = severalStores(linked);
  const fedBy = (installationId: string, websiteCode: string) => {
    const entry = linked.find((l) => l.key === websiteKey(installationId, websiteCode));
    return entry ? websiteLabel(entry.store, entry.website, withStore) : 'Store';
  };

  const columns: Column<ErpInventoryRow>[] = [
    {
      id: 'article',
      header: 'Article',
      card: 'title',
      width: 'fill',
      cell: (row) => (
        <span className="cell-stack">
          {row.name ? <span className="cell-stack__primary">{row.name}</span> : <span className="muted">No name in the ERP</span>}
          {row.barcode ? <span className="cell-stack__secondary">Barcode {row.barcode}</span> : null}
          {row.sellable === false ? <span className="cell-stack__secondary">Not for sale in the ERP</span> : null}
          {/* Kept at its last quantity, never sent as zero. */}
          {row.missing_since ? <span className="erp-missing">Not reported since {formatWhen(row.missing_since)}</span> : null}
        </span>
      ),
    },
    { id: 'code', header: 'Code', width: 12, cell: (row) => <code>{row.article_code}</code> },
    {
      id: 'warehouse',
      header: 'Warehouse',
      width: 12,
      cell: (row) => (
        <span className="cell-stack">
          <code>{row.warehouse_code}</code>
          {row.warehouse_name ? <span className="cell-stack__secondary">{row.warehouse_name}</span> : null}
        </span>
      ),
    },
    {
      id: 'quantity',
      header: <span className="rt-num">Quantity</span>,
      cardLabel: 'Quantity',
      width: 10,
      cell: (row) => (
        <span className="rt-num tabular-nums">{formatDecimal(row.quantity)}</span>
      ),
    },
    { id: 'unit', header: 'Unit', width: 8, cell: (row) => row.unit || <span className="muted">Not set</span> },
    {
      id: 'price',
      header: <span className="rt-num">Sale price</span>,
      cardLabel: 'Sale price',
      width: 14,
      cell: (row) =>
        row.card_price ? (
          <CardPriceCell row={row} />
        ) : (
          <span className="rt-num tabular-nums">
            {row.sale_price ? formatDecimal(row.sale_price, 2) : <span className="muted">No sale price</span>}
            {row.vat_rate ? <span className="cell-stack__secondary">VAT {formatDecimal(row.vat_rate)}%</span> : null}
          </span>
        ),
    },
    {
      id: 'products',
      header: 'Store products',
      width: 'fill',
      cell: (row) =>
        row.products.length === 0 ? (
          <span className="muted">No matched products</span>
        ) : (
          <ul className="erp-product-list">
            {row.products.map((product) => (
              <li key={`${product.installation_id}:${product.website_code}:${product.sku}`}>
                <code>{product.sku}</code>
                {linked.length > 1 ? ` in ${fedBy(product.installation_id, product.website_code)}` : ''}
                {` → ${product.location}`}
                <span className="cell-stack__secondary">
                  {product.quantity_sent !== undefined ? `${formatDecimal(product.quantity_sent)} sent` : 'Not sent yet'}
                  {product.price_sent !== undefined ? `, price ${formatDecimal(product.price_sent, 2)}` : ''}
                  {product.held_price !== undefined ? `, price ${formatDecimal(product.held_price, 2)} held` : ''}
                </span>
              </li>
            ))}
          </ul>
        ),
    },
    { id: 'changed', header: 'Changed', width: 'time', priority: 'secondary', cell: (row) => <Timestamp iso={row.changed_at} /> },
  ];

  return (
    <PanelCard labelledBy="erp-inventory-title" testId="erp-inventory">
      <SectionHeader
        id="erp-inventory-title"
        title="Inventory"
        description={
          <span className="erp-count-line" data-testid="erp-inventory-count">
            {formatNumber(connection.positions)} {connection.positions === 1 ? 'position' : 'positions'}
            <HelpTip label="What is an inventory position?">
              One article in one warehouse. The same article in multiple warehouses counts separately.
            </HelpTip>
          </span>
        }
        help={
          <p>
            A store takes stock only from the warehouses it maps; the quantity it is sent is the sum of every warehouse
            mapped to the same place. An article the ERP stops reporting keeps its last quantity and is never sent as zero.
          </p>
        }
        helpLabel="How inventory works"
      />
      <div className="erp-filters">
        <Field label="Search">
          <Input
            id="erp-inventory-search"
            type="search"
            value={search}
            placeholder="Article code, name, barcode or SKU…"
            autoComplete="off"
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>
        {warehouses.length > 1 ? (
          <Field label="Warehouse">
            <Select
              id="erp-inventory-warehouse"
              value={warehouse}
              onChange={(event) => {
                setWarehouse(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">All warehouses</option>
              {warehouses.map((w) => (
                <option key={w.code} value={w.code}>
                  {w.name ? `${w.code} ${w.name}` : w.code}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Status">
          <Select
            id="erp-inventory-filter"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value as InventoryFilter);
              setOffset(0);
            }}
          >
            {(Object.keys(filterLabel) as InventoryFilter[]).map((value) => (
              <option key={value || 'all'} value={value}>
                {filterLabel[value]}
                {value ? ` (${formatNumber(counts[value] ?? 0)})` : ''}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <ErrorNotice error={inventory.error} />
      {inventory.loading && rows.length === 0 ? (
        <LoadingRows label="Loading inventory" rows={6} />
      ) : rows.length === 0 ? (
        <QuietEmpty testId="erp-inventory-empty">{connection.positions === 0 ? 'Nothing synced yet' : 'No articles match'}</QuietEmpty>
      ) : (
        <>
          <ResponsiveTable
            rows={rows}
            rowKey={(row) => `${row.warehouse_code}:${row.article_code}`}
            caption="ERP articles and the store products they feed"
            className="erp-inventory-table"
            columns={columns}
          />
          <Pager
            first={offset + 1}
            last={offset + rows.length}
            total={total}
            onPrevious={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            onNext={() => setOffset(offset + PAGE_SIZE)}
          />
        </>
      )}
    </PanelCard>
  );
}
